import { useState } from 'react';
import { Eye, EyeOff, LogOut, Sparkles } from 'lucide-react';

export default function AuthCard({
  isRegistering,
  loginForm,
  setLoginForm,
  authError,
  onSubmit,
  onToggleMode,
}: {
  isRegistering: boolean;
  loginForm: { username: string; password: string };
  setLoginForm: (value: { username: string; password: string }) => void;
  authError: string;
  onSubmit: () => void;
  onToggleMode: () => void;
}) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="auth-card p-8 max-w-md w-full relative overflow-hidden">
        <div className="absolute -top-10 -right-10 h-36 w-36 rounded-full bg-[var(--accent-muted)] blur-2xl pointer-events-none" />
        <div className="absolute -bottom-14 -left-12 h-40 w-40 rounded-full bg-[var(--accent-glow)] blur-2xl pointer-events-none" />

        <div className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-[var(--bg-tertiary)] px-3 py-1 text-[11px] uppercase tracking-[0.12em] text-[var(--text-secondary)] mb-4">
          <Sparkles size={12} />
          Campaign Workspace
        </div>

        <h1 className="text-3xl font-bold text-center mb-2 text-[var(--text-primary)] app-title">
          Campaign Tracker
        </h1>
        <p className="text-center text-[var(--text-secondary)] mb-6">
          {isRegistering ? 'Create Your Account' : 'Welcome Back, Adventurer'}
        </p>
        <p className="text-center text-xs text-[var(--text-muted)] mb-3">
          Registration access depends on server policy for this deployment.
        </p>
        <p className="text-center text-[11px] text-[var(--text-muted)] mb-6">
          Username matching is case-insensitive and ignores surrounding spaces.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[var(--accent-text)] mb-1">Username</label>
            <input
              data-testid="auth-username-input"
              type="text"
              value={loginForm.username}
              onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
              className="w-full p-3 input-field"
              placeholder="name or email"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[var(--accent-text)] mb-1">Password</label>
            <div className="relative">
              <input
                data-testid="auth-password-input"
                type={showPassword ? 'text' : 'password'}
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
                className="w-full p-3 pr-11 input-field"
                placeholder="Enter your password"
                autoComplete={isRegistering ? 'new-password' : 'current-password'}
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)] p-1"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {authError && (
            <div className="rounded-md border border-[var(--danger)] bg-[var(--danger-muted)] px-3 py-2 text-[var(--danger)] text-sm text-center">
              {authError}
            </div>
          )}

          <button
            data-testid="auth-submit-button"
            onClick={onSubmit}
            className="w-full btn-primary-solid"
          >
            {isRegistering ? 'Register' : 'Login'}
          </button>

          <button
            onClick={onToggleMode}
            className="w-full text-button text-button-muted text-sm"
          >
            {isRegistering ? 'Already have an account? Login' : "Don't have an account? Register"}
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-[var(--border-default)]">
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <LogOut size={12} /> Your data is saved locally and securely
          </div>
        </div>
      </div>
    </div>
  );
}
